import styled from 'styled-components';
import { Compound, DefaultMapEntity, ResultsType } from '../../types';
import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import useMapHelpers from '../../hooks/MapHelpers';
import { points } from '@turf/helpers';

type Props = {
  isFetching: boolean;
};

const LoadingChip = ({ isFetching }: Props) => {
  const { isPointWithinBounds } = useMapHelpers();
  const compounds = useSelector((state: any) => state.map.compounds);
  const areas = useSelector((state: any) => state.map.areas);
  const resultsType = useSelector((state: any) => state.map.resultsType);
  const filters = useSelector((state: any) => state.map.filters);
  const { locale } = useRouter();
  const visiblePoints: any = isPointWithinBounds(
    points(compounds.map((compound: Compound) => compound?.coordinates))
  );
  const visibleCompounds = compounds.filter((compound: any) => {
    return visiblePoints.find(
      (point: any) => point?.geometry?.coordinates === compound.coordinates
    );
  });

  const getChipTitle = () => {
    const totalResults =
      resultsType === ResultsType.COMPOUNDS
        ? visiblePoints?.length
        : areas?.length;
    if (Object.keys(filters)?.length > 0) {
      const filteredResults =
        resultsType === ResultsType.COMPOUNDS
          ? visibleCompounds.filter(
              (compound: DefaultMapEntity) => compound.isFiltered
            )
          : areas.filter((area: DefaultMapEntity) => area.isFiltered);

      if (filteredResults?.length == 0) {
        return t('no_matching_results');
      } else {
        return `${filteredResults.length} ${t('out_of')} ${totalResults} ${t(
          'results'
        )}`;
      }
    } else if (totalResults > 0) {
      if (resultsType === ResultsType.COMPOUNDS) {
        return `${totalResults} ${t('compounds')}`;
      } else if (resultsType === ResultsType.AREAS) {
        return `${totalResults} ${t('areas')}`;
      }
    }
    return t('no_results');
  };
  const { t } = useTranslation('common');
  const renderResults = useCallback(() => {
    if (isFetching) {
      return <span>{t('loading')}</span>;
    }

    return <span>{getChipTitle()}</span>;
  }, [isFetching, resultsType, compounds, areas, filters, visibleCompounds]);

  return <Container locale={locale}>{renderResults()}</Container>;
};

const Container = styled.div<{ locale?: string }>`
  position: absolute;
  bottom: 38px;
  inset-inline-start: 16px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.79);
  box-shadow:
    0px 0px 4px 0px rgba(0, 0, 0, 0.04),
    0px 4px 8px 0px rgba(0, 0, 0, 0.06);
  border-radius: 360px;
  height: 40px;
  display: flex;
  align-items: center;
  span {
    font-size: 14px;
    font-family: materialMedium;
    color: #000;
  }
  z-index: 9999;
  @media screen and (max-width: 768px) {
    bottom: 90px;
    inset-inline-start: 50%;
    transform: ${props =>
      props.locale == 'en' ? 'translateX(-50%)' : 'translateX(50%)'};
  }
`;

export default LoadingChip;
